import richTextUtils from "../../utils/richtext.utils";
import FileUploaderModal from "@plugins/file-uploader-v2/components/FileUploaderModal.vue";
import MediaObjectService from "@plugins/richtext/service/media.object.service";
import i18n from "@plugins/i18n";
import MediaSettingsSubpage from "@/plugins/media/components/MediaSettingsSubpage.vue";
import DOMUtils from "@utils/dom.utils";
import mediaObjectApi from "@apis/media.object.api";
import { FileResponse, UploadFlow } from "@plugins/file-uploader-v2/types.ts";
import { MediaObjectResponse } from "@plugins/media/types.ts";
import randomUtils from "@utils/random.utils.ts";
import html5InteractiveEventBus from "./service/event-bus";

export default function (editor) {
  const MEDIA_SELECTOR = ".sls-media[data-type='mo'], .sls-media-object";
  const richTextEditor = editor.options.get("richTextEditor");
  const context = richTextEditor.richTextContext;
  const pluginEvent = editor.options.get("pluginEvent");
  let mediaObjectService: MediaObjectService | null = null;
  let htmlMOUuid = null;

  editor.ui.registry.addButton("html5_interactive", {
    tooltip: i18n.t("tinymce_plugin_html5_interactive_add_button_tooltip"),
    icon: "html5Interactive",
    onAction: function () {
      openHtml5Modal();
    },
  });

  editor.addCommand("mceHTML5MediaDelete", (targetNode) => {
    richTextUtils.removeNodeFromEditor(editor, targetNode);
  });

  editor.on("click", (e) => {
    const target = e.target;
    if (target.closest("button.trash") && target.closest("button.trash").length !== 0) {
      const targetNode = target.closest(".sls-media-wrapper");
      //to go throught execCommand, so that tinymce can control the delete event
      if (targetNode) editor.execCommand("mceHTML5MediaDelete", targetNode);
    }

    // cater for cases when have undo action
    handlePluginRerender(target);
  });

  (function init() {
    richTextEditor.plugins["html5_interactive"] = {
      renderInEditor,
      cleanUp,
      containPluginElements,
      openHtml5Modal,
      initializeHTML5MOUuid,
    };
    mediaObjectService = new MediaObjectService(context);
  })();

  function initializeHTML5MOUuid(value) {
    htmlMOUuid = value;
  }

  function openHtml5Modal() {
    if (containPluginElements(editor.getElement())) {
      richTextEditor.$modal.showMessage("info", {
        title: i18n.t("question_component_error_existing_html5_interactive_component_title"),
        content: i18n.t("question_component_error_existing_html5_interactive_component_subtitle"),
        showClose: false,
        use_secondaryButton: false,
      });
      return;
    }

    richTextEditor.$modal.show(
      FileUploaderModal,
      {
        uploadFlow: UploadFlow.ZIP,
        modalId: randomUtils.randomId(),
      },
      {
        "update:fileEntry": handleFileUpload,
      },
      true,
    );
  }

  function openSettings(settings) {
    window.tinymce.currentEditor = editor;
    settings.editorSaveCallback = editorSaveCallback;
    settings.isHTML5Interactive = !!richTextEditor.html5MOUuid;
    richTextEditor.$contentSubpage.show(MediaSettingsSubpage, settings);
  }
  function openPdfInNewTab(uuid) {
    const routeData = richTextEditor.$router.resolve({
      name: "PdfView",
      params: { uuid: uuid },
    });
    window.open(routeData.href, "_blank");
  }
  function handleFileUpload(fileResponse: FileResponse) {
    // Send information to backend that this file is capable of marking
    htmlMOUuid = null;
    mediaObjectService?.create(fileResponse, true).then(
      (data: MediaObjectResponse) => {
        richTextEditor.$modal.removeAll();
        richTextUtils.insertInlineAttachment(editor, {
          uuid: data.uuid,
          mediaType: data.fileType,
          fileName: fileResponse.name,
          type: mediaObjectService?.getType(),
          isAddNewMedia: true,
        });
        richTextUtils.renderInlineMedia(editor, ".inline-placeholder", {
          onMediaSettings: openSettings,
          onOpenPdfInNewTab: openPdfInNewTab,
        });
        editor.save();
        pluginEvent?.$emit("mediaRendered");

        mediaObjectApi.getMetaData({ uuid: data.uuid }).then((metadataResponse) => {
          if (metadataResponse && metadataResponse.data && metadataResponse.data.markable) {
            htmlMOUuid = data.uuid;
            richTextEditor.$emit("changeHtml5Interactive", data.uuid);
            html5InteractiveEventBus.$emit("html5ItemAdded", { uuid: data.uuid });
            mediaObjectService?.getMetaDataWhenFileProcessed(data.uuid).then((readyResponse) => {
              if (readyResponse && !readyResponse.markable) {
                htmlMOUuid = null;
                richTextEditor.$emit("changeHtml5Interactive", null);
                html5InteractiveEventBus.$emit("html5ItemRemoved");
              }
            });
          }
        });
      },
      () => {
        event.onFail();
      },
    );
  }

  function renderInEditor(rerender = false) {
    // convert any legacy objects
    richTextUtils.convertLegacyMediaObjects(editor.getBody());
    richTextUtils.convertLegacyImageMediaObjects(editor.getBody());
    richTextUtils.renderInlineMedia(
      editor,
      MEDIA_SELECTOR,
      {
        onMediaSettings: openSettings,
        onOpenPdfInNewTab: openPdfInNewTab,
      },
      rerender,
    );
  }

  function editorSaveCallback() {
    if (window.tinymce && window.tinymce.currentEditor) {
      window.tinymce.currentEditor.save();
      delete window.tinymce.currentEditor;
    }
  }

  function cleanUp(content) {
    const container = document.createElement("div");
    const containerAfterCleanUp = document.createElement("div");
    container.innerHTML = content;
    containerAfterCleanUp.innerHTML = content;

    // remove placeholder
    DOMUtils.elOf(containerAfterCleanUp, ".sls-media-wrapper").forEach((element) => {
      if (DOMUtils.elOf(element, ".inline-placeholder").length !== 0) {
        element.remove();
      }
    });
    DOMUtils.elOf(containerAfterCleanUp, ".inline-placeholder").forEach((element) => {
      element.remove();
    });
    DOMUtils.elOf(container, ".sls-media-tag[data-type='mo']").forEach((element) => {
      cleanUpElement(containerAfterCleanUp, element);
    });

    DOMUtils.elOf(containerAfterCleanUp, "br[data-mce-bogus]").forEach((element) => {
      element.remove();
    });

    return containerAfterCleanUp.innerHTML;
  }

  function cleanUpElement(container, element) {
    const uuid = element.getAttribute("data-uuid");
    const media = DOMUtils.elOf(container, `.sls-media-tag[data-uuid='${uuid}']`)[0];
    const mediaWrapper = media.closest(".sls-media-wrapper");

    // switch back the class
    DOMUtils.removeClass(element, "sls-media-tag");
    DOMUtils.addClass(element, "sls-media");
    mediaWrapper?.replaceWith(element);
  }

  function containPluginElements(container) {
    return (
      container.querySelectorAll(".sls-media, .sls-media-object, figure.media").length > 0 &&
      htmlMOUuid
    );
  }

  function handlePluginRerender(target) {
    let mediaComponent = target.closest(".media-component");
    if (!mediaComponent || mediaComponent.length === 0 || mediaComponent.__vue__) return;

    const targetNode = mediaComponent.closest(".sls-media-wrapper")
      ? DOMUtils.elOf(
          mediaComponent.closest(".sls-media-wrapper"),
          ".sls-media-tag[data-type='mo']",
        )[0]
      : null;
    if (!targetNode) return;

    const parentMediaComponent = mediaComponent.closest(".paragraph");
    const targetComponentIndex = DOMUtils.elOf(parentMediaComponent, ".media-component").indexOf(
      mediaComponent,
    );

    cleanUpElement(editor.getBody(), targetNode);
    renderInEditor(true);

    mediaComponent = DOMUtils.elOf(parentMediaComponent, ".media-component")[targetComponentIndex];
    const mediaVueComponent = mediaComponent.__vue__;
    if (mediaVueComponent) {
      // enable drawing plugin
      pluginEvent?.$emit("mediaRendered");
    }
  }
}
